import React from 'react';
import styled from 'styled-components';
import { RiSignalWifiLine, RiRefreshLine, RiRouterLine } from '@remixicon/react';
import { colors } from '@/constants/colors';
import type { Antenna, RejoinRate, Signal } from '@/utils/linkHealth';

/**
 * Signal and rejoin rate, rendered once and used in both list views.
 *
 * Same reasoning as UptimeCell: an operator quoting "-72, Weak" in a support
 * message has to mean what the admin sees on a different screen, and separate
 * JSX would drift on the first change.
 *
 * A box with nothing to report renders an em dash. That covers wired boxes
 * (which have no radio at all) and boxes too freshly booted for a rate to mean
 * anything — never a zero, which would read as "perfect" for exactly the boxes
 * we know least about.
 */

const tone = (severity: 0 | 1 | 2) =>
   severity === 2 ? colors.RED : severity === 1 ? colors.WARNING : colors.SUCCESS;

const Wrap = styled.span<{ $severity: 0 | 1 | 2 }>`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   /* Never wrap — the lesson from "Offline 12d 16h" becoming three lines. */
   white-space: nowrap;
   font-variant-numeric: tabular-nums;
   font-weight: 600;
   color: ${({ $severity }) => tone($severity)};
`;

const Band = styled.span`
   font-weight: 500;
   opacity: 0.85;
`;

const Empty = styled.span`
   color: ${colors.GRAY5};
`;

export const SignalCell = ({
   signal,
   compact,
}: {
   signal: Signal | null | undefined;
   /**
    * Drop the band word and keep the number. The device CARD puts this beside
    * four other stats in a fixed grid, and "-73 (Weak)" ran past the card edge
    * (Ray, 2026-08-23). The band still reads on hover, and the colour already
    * carries the same meaning at a glance. The LIST has room, so it keeps the
    * word — that is where someone scans for "which of these is weak".
    */
   compact?: boolean;
}) => {
   if (!signal)
      return (
         <Empty title="No radio reading — this box is wired, or has not reported one">
            —
         </Empty>
      );
   return (
      <Wrap
         $severity={signal.severity}
         title={signal.title}
         aria-label={`Signal ${signal.dbm} dBm, ${signal.label}`}
      >
         <RiSignalWifiLine size={14} aria-hidden />
         {signal.dbm}
         {!compact && <Band>({signal.label})</Band>}
      </Wrap>
   );
};

export const RejoinCell = ({ rate }: { rate: RejoinRate | null | undefined }) => {
   if (!rate)
      return (
         <Empty title="Not enough uptime yet for this to mean anything (needs 30 minutes), or this box is wired">
            —
         </Empty>
      );
   return (
      <Wrap
         $severity={rate.severity}
         title={rate.title}
         aria-label={`${rate.display} reconnects per hour`}
      >
         <RiRefreshLine size={14} aria-hidden />
         {rate.display}
         <Band>/hr</Band>
      </Wrap>
   );
};

/**
 * Which antenna the NodeMCU is on.
 *
 * ⚠️ Never say "captured" here. The operator's own extender is separate
 * hardware with its own address, so a perfectly legitimate shop reads as
 * "away" — and accusing a customer of being hacked because they bought a range
 * extender is worse than saying nothing.
 */
export const AntennaCell = ({ antenna }: { antenna: Antenna | null | undefined }) => {
   if (!antenna) return <Empty title="Wired box — no antenna">—</Empty>;
   if (antenna.state === 'unknown')
      return <Empty title={antenna.detail}>—</Empty>;
   return (
      <Wrap
         $severity={antenna.severity}
         title={`${antenna.detail}\n\n${antenna.bssid}`}
         aria-label={antenna.label}
      >
         <RiRouterLine size={14} aria-hidden />
         {antenna.label}
      </Wrap>
   );
};
